<template>
    <div>
        <modal
            :append-to-body="true"
            v-bind="attrs"
            v-on="$listeners"
            @close="onCloseDialog"
            :width="modalWidth"
            id="componentStep"
            top="50px"
        >
            <template v-slot:title>
                <span class="form-title main-text-color">
                    {{ $t('widget.widgetAdd') }}
                </span>
            </template>
            <template v-slot:additional-action>
                <portal-target name="additional-action"/>
            </template>
            <fade-transition
                :duration="transitionDuration"
                mode="out-in"
            >
                <widget-list
                    :widgetList="widgetList"
                    class="widget-list"
                    @select-widget="selectWidget"
                    @add-all-widgets-from-group="addAllWidgetsFromGroup"
                />
            </fade-transition>
            <template v-slot:footer>
                <div class="main-bg-light border-t-2 border-gray-300 py-4 flex items-center justify-between">
                    <div class="px-6">
                        <p class="text-xs leading-4 main-text-color font-bold mb-1">{{ getSummaryActions }}</p>
                    </div>
                    <div class="px-10">
                        <el-button
                            @click="submitWidgets"
                            :disabled="submitDisabled"
                            class="button-set-up-widgets font-bold"
                            type="primary"
                        >
                            {{ $t('general.add') }}
                        </el-button>
                    </div>
                </div>
            </template>
        </modal>

        <ConfirmDialog
            :visible.sync="showConfirmDialog"
            top="18vh"
            @on-close="onCancel"
        >
            <template v-slot:title>
                <h3 class="text-main-2xl font-semibold main-text-color">
                    {{ $t('report.addWidgetsTitle') }}
                </h3>
            </template>
            <div class="flex justify-center w-full">
                <div class="text-center main-text-color text-main-sm leading-21 my-6 max-w-65-p px-3">
                    {{ $tc('report.addWidgetsDesc', selectedWidgets.length, {count: selectedWidgets.length}) }}
                </div>
            </div>
            <template v-slot:footer-actions>
                <slot name="footer-actions">
                    <base-button
                        outline
                        fixed-width="w-32"
                        @click="onCancel">
                        <div class="flex items-center">
                            <IconDiscard class="mx-1"/>
                            <span class="mx-1 text-base font-bold">{{ $t('common.cancel') }}</span>
                        </div>
                    </base-button>
                    <base-button
                        type="primary"
                        @click="onConfirm"
                        fixed-width="w-32"
                        key="store"
                    >
                        <span class="mx-1 text-base font-bold">{{ $t('common.confirm') }}</span>
                    </base-button>
                </slot>
            </template>
        </ConfirmDialog>
    </div>
</template>

<script>
import Modal from '@/components/Common/Modal'
import WidgetList from "@/components/Reports/Widgets/AddWidgetsForm/WidgetList";
import ConfirmDialog from '@/components/Common/ConfirmDialog'
import { reportApi } from '@/modules/reports/services/reportService'
import cloneDeep from "lodash/cloneDeep"

const LimitOfAddingWidgets = 50

export default {
    components: {
        Modal,
        WidgetList,
        ConfirmDialog,
    },
    props: {
        modalWidth: {
            type: String,
            default: '1210px',
        },
        widgets: {
            type: Array,
            default: () => []
        },
        transitionDuration: 100,
        visible: {
            type: Boolean,
            default: false
        },
        reportId: {
            type: [Number, String],
            default: ''
        },
        doRequest: {
            type: Boolean,
            default: true
        },
        widgetsBefore: {
            type: Number,
            default: 0
        }
    },
    data () {
        return {
            showConfirmDialog: false,
            selectedWidgets: [],
            widgetList: []
        }
    },
    computed: {
        attrs() {
            return {
                ...this.$attrs,
                visible: this.visible
            }
        },
        getSummaryActions() {
            const widgetsBefore = this.widgetsBefore
            const widgetsAfter = this.selectedWidgets.length
            return `${this.$t('widget.summary')}: (${this.$t('widget.before')} - ${ widgetsBefore }, ${this.$t('widget.afterAdding')} - ${ widgetsAfter }) - ${this.$t('general.limit')} (50)`
        },
        submitDisabled () {
            return !this.selectedWidgets.length || !this.widgetList.length || (this.selectedWidgets.length + this.widgetsBefore) > LimitOfAddingWidgets
        }
    },
    mounted () {
        this.widgetList = cloneDeep(this.widgets)
    },
    methods: {
        onCloseDialog() {
            this.$emit('update:visible', false)
            this.resetDialog()
        },
        resetDialog() {
            this.widgetList = []
        },
        async onConfirm () {
            if (this.submitDisabled) {
                return
            }
            const location = window.location.pathname.search('/edit/')
            let selectedWidgetsWithReportItemID = []
            if (this.doRequest || location !== -1) {
                const selectedWidgets = this.selectedWidgets.map(async el => {
                    const data = {
                        ReportID: this.reportId,
                        WidgetID: el
                    }

                    const { ReportItemID } = await reportApi.itemUpsert(data)
                    return {
                        ReportItemID,
                        WidgetID: el
                    }
                })

                selectedWidgetsWithReportItemID = await Promise.all(selectedWidgets)
            }

            const activeWidgetItems = this.widgetList
                .filter(el => this.selectedWidgets.includes(el.WidgetID))
                .map(el => {
                    delete el.isChecked
                    if (selectedWidgetsWithReportItemID.length) {
                        el.ReportItemID = selectedWidgetsWithReportItemID.find(item => item.WidgetID === el.WidgetID).ReportItemID
                    }
                    if (this.reportId) {
                        el.ReportID = this.reportId
                    }
                    return el
                })

            this.$emit('added-widgets', {
                activeWidgetItems: activeWidgetItems
            })
            this.showConfirmDialog = false
            this.$emit('update:visible', false)
        },
        onCancel () {
            this.showConfirmDialog = false
        },
        selectWidget (widgetId) {
            const isSelected = this.selectedWidgets.includes(widgetId)

            if (!isSelected) {
                const widgetIndex = this.selectedWidgets.indexOf(widgetId)
                if (widgetIndex === -1) {
                    this.selectedWidgets.push(widgetId)
                }
            } else {
                const widgetIndex = this.selectedWidgets.indexOf(widgetId)
                this.selectedWidgets.splice(widgetIndex, 1)
            }
        },
        addAllWidgetsFromGroup () {
            if (this.widgetList.every(widget => widget.isChecked)) {
                return
            }
            this.selectedWidgets = []
            this.widgetList = this.widgetList.map(widget => {
                widget.isChecked = true
                this.selectWidget(widget.WidgetID)
                return widget
            })
        },
        submitWidgets () {
            this.showConfirmDialog = true
        }
    },
    watch: {
        async visible (val) {
            if (val) {
                this.widgetList = cloneDeep(this.widgets)
            } else {
                this.widgetList = []
                this.selectedWidgets = []
            }
        }
    },
    async beforeDestroy () {
        this.resetDialog()
        this.showConfirmDialog = false
    }
}
</script>

<style lang="scss">
.redirect-action {
    @apply text-sm flex items-center px-8 cursor-pointer;
}
.form-title {
    @apply font-bold text-xl;
    color: var(--mainTextColor-color) !important;
}
.widget-list {
    height: 480px;
}
body ::v-deep.el-loading-mask {
    height: 480px !important;
}
</style>
